import { useLiveQuery } from "dexie-react-hooks";
import { useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { compareMonths, monthsOfYear } from "@/domain/accounting-period";
import { computeBalanceSeries } from "@/finance/balance";
import { computeYear } from "@/finance/year";
import { deleteBalanceAnchor, saveBalanceAnchor } from "@/data/planning";
import { formatEurPlain, formatEurSigned, formatMonth, formatMonthShort, formatMonthTitle, formatPercent } from "@/lib/format";
import { LineChart } from "@/ui/charts";
import { Button, ConfirmButton, Field, Notice, Panel, PeriodSwitcher, TextInput } from "@/ui/primitives";
import { db, useFinanceIndex, useToday } from "../hooks";

export function BalancePage() {
  const { month: currentMonth, year: currentYear } = useToday();
  const [params] = useSearchParams();
  const yearParam = Number(params.get("anno"));
  const year = Number.isInteger(yearParam) && yearParam > 1900 ? yearParam : currentYear;
  const index = useFinanceIndex();
  const anchors = useLiveQuery(() => db.balanceAnchors.orderBy("effectiveMonth").toArray(), []);

  const [form, setForm] = useState({ id: "", effectiveMonth: `${currentYear}-01`, amount: "", label: "Saldo di partenza" });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (!index || !anchors) return <p className="text-muted">Caricamento…</p>;

  const months = monthsOfYear(year);
  const visible = year === currentYear ? months.filter((m) => compareMonths(m, currentMonth) <= 0) : months;
  const series = computeBalanceSeries(index, visible[0]!, visible[visible.length - 1]!);
  const now = computeBalanceSeries(index, currentMonth, currentMonth)[0]!;

  const years = [...new Set([...index.dataMonths, ...anchors.map((a) => a.effectiveMonth)].map((m) => Number(m.slice(0, 4))))].sort();
  const yearly = years.map((y) => computeYear(index, y));

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await saveBalanceAnchor(db, { id: form.id || undefined, effectiveMonth: form.effectiveMonth, amount: form.amount, label: form.label });
      setForm({ id: "", effectiveMonth: form.effectiveMonth, amount: "", label: "Saldo di partenza" });
      setSaved(true);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <section>
        <p className="text-muted">Saldo attuale, a fine {formatMonth(currentMonth)}</p>
        <p className="amount text-5xl font-semibold tracking-tight">{formatEurPlain(now.closing)}</p>
        <p className="mt-1 text-sm text-muted">
          Saldo iniziale + entrate − spese, mese per mese. Il saldo di fine mese è il saldo iniziale del mese successivo.
        </p>
        {!now.anchored ? (
          <Notice>Non c&apos;è ancora un saldo di partenza: il calcolo parte da zero. Aggiungilo qui sotto.</Notice>
        ) : null}
      </section>

      <PeriodSwitcher label={String(year)} previousHref={`/saldo?anno=${year - 1}`} nextHref={`/saldo?anno=${year + 1}`} previousLabel={`Anno ${year - 1}`} nextLabel={`Anno ${year + 1}`}>
        <p className="text-sm text-muted">Evoluzione del saldo</p>
      </PeriodSwitcher>

      <Panel title="Saldo a fine mese">
        <LineChart
          ariaLabel={`Saldo a fine mese nel ${year}`}
          labels={series.map((r) => formatMonthShort(r.month))}
          tooltipTitles={series.map((r) => formatMonthTitle(r.month))}
          values={series.map((r) => Number(r.closing))}
          valueLabel="Saldo"
        />
        <div className="-mx-4 mt-3 overflow-x-auto px-4">
          <table className="w-full min-w-[34rem] text-sm">
            <thead className="text-left text-muted">
              <tr>
                <th className="py-1.5 font-normal">Mese</th>
                <th className="py-1.5 text-right font-normal">Iniziale</th>
                <th className="py-1.5 text-right font-normal">Entrate</th>
                <th className="py-1.5 text-right font-normal">Spese</th>
                <th className="py-1.5 text-right font-normal">Finale</th>
              </tr>
            </thead>
            <tbody>
              {series.map((r) => (
                <tr key={r.month} className="border-t border-line">
                  <td className="py-1.5">
                    {formatMonthTitle(r.month)}
                    {r.anchorLabel ? <span className="block text-xs text-muted">{r.anchorLabel}</span> : null}
                  </td>
                  <td className="amount py-1.5 text-right">{formatEurPlain(r.opening)}</td>
                  <td className="amount py-1.5 text-right">{formatEurPlain(r.income)}</td>
                  <td className="amount py-1.5 text-right">{formatEurPlain(r.expenses)}</td>
                  <td className="amount py-1.5 text-right font-medium">{formatEurPlain(r.closing)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Anno per anno">
        {yearly.length === 0 ? (
          <p className="text-sm text-muted">Nessun dato.</p>
        ) : (
          <div className="-mx-4 overflow-x-auto px-4">
            <table className="w-full min-w-[30rem] text-sm">
              <thead className="text-left text-muted">
                <tr>
                  <th className="py-1.5 font-normal">Anno</th>
                  <th className="py-1.5 text-right font-normal">Inizio</th>
                  <th className="py-1.5 text-right font-normal">Risparmio</th>
                  <th className="py-1.5 text-right font-normal">Fine</th>
                  <th className="py-1.5 text-right font-normal">Variazione</th>
                </tr>
              </thead>
              <tbody>
                {yearly.map((y) => (
                  <tr key={y.year} className="border-t border-line">
                    <td className="py-1.5">{y.year}</td>
                    <td className="amount py-1.5 text-right">{formatEurPlain(y.balance.opening)}</td>
                    <td className="amount py-1.5 text-right">{formatEurSigned(y.totals.savings)}</td>
                    <td className="amount py-1.5 text-right font-medium">{formatEurPlain(y.balance.closing)}</td>
                    <td className="amount py-1.5 text-right">{formatPercent(y.balance.changeRatio)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Saldi di riferimento">
        <p className="mb-3 text-sm text-muted">
          Un saldo di riferimento fissa il saldo iniziale di un mese (per esempio il saldo di partenza dell&apos;anno, o un saldo verificato sul conto).
          I mesi successivi partono da lì.
        </p>
        {anchors.length > 0 ? (
          <ul className="mb-4 divide-y divide-line">
            {anchors.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm">
                <span>
                  <span className="font-medium">{formatMonthTitle(a.effectiveMonth)}</span> — {a.label}
                  {a.source === "excel_import" ? <span className="text-muted"> (da Excel)</span> : null}
                </span>
                <span className="flex items-center gap-2">
                  <span className="amount font-medium">{formatEurPlain(a.amount)}</span>
                  <Button variant="ghost" onClick={() => setForm({ id: a.id, effectiveMonth: a.effectiveMonth, amount: a.amount.replace(".", ","), label: a.label })}>
                    Modifica
                  </Button>
                  <ConfirmButton label="Elimina" confirmLabel="Elimina il saldo" onConfirm={() => deleteBalanceAnchor(db, a.id)} />
                </span>
              </li>
            ))}
          </ul>
        ) : null}
        <form onSubmit={submit} className="grid gap-3 sm:grid-cols-3">
          <Field label="Dal mese">
            <TextInput type="month" required value={form.effectiveMonth} onChange={(e) => setForm({ ...form, effectiveMonth: e.target.value })} />
          </Field>
          <Field label="Saldo iniziale (€)">
            <TextInput inputMode="decimal" required placeholder="1.500,00" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </Field>
          <Field label="Descrizione">
            <TextInput required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </Field>
          <div className="flex flex-wrap items-center gap-3 sm:col-span-3">
            <Button type="submit">{form.id ? "Salva modifiche" : "Aggiungi saldo"}</Button>
            {form.id ? (
              <Button variant="ghost" onClick={() => setForm({ id: "", effectiveMonth: `${currentYear}-01`, amount: "", label: "Saldo di partenza" })}>
                Annulla modifica
              </Button>
            ) : null}
            {saved ? <span className="text-sm text-under">Saldo salvato.</span> : null}
          </div>
          {error ? <div className="sm:col-span-3"><Notice tone="error">{error}</Notice></div> : null}
        </form>
      </Panel>
    </div>
  );
}
